type ValueOfType<ObjectType> = ObjectType[keyof ObjectType];

/**
 * Mostly taken from
 * https://gist.githubusercontent.com/scokmen/f813c904ef79022e84ab2409574d1b45/raw/cd8709a2fccb005bb53e9bfb2461e07d40b4e8d8/HttpStatusCode.ts
 */
export const HTTP_STATUS_CODES = {
  CONTINUE: 100,
  SWITCHING_PROTOCOLS: 101,
  PROCESSING: 102,
  OK: 200,
  CREATED: 201,
  ACCEPTED: 202,
  NON_AUTHORITATIVE_INFORMATION: 203,
  NO_CONTENT: 204,
  RESET_CONTENT: 205,
  PARTIAL_CONTENT: 206,
  MULTI_STATUS: 207,
  ALREADY_REPORTED: 208,
  IM_USED: 226,
  MULTIPLE_CHOICES: 300,
  MOVED_PERMANENTLY: 301,
  FOUND: 302,
  SEE_OTHER: 303,
  NOT_MODIFIED: 304,
  USE_PROXY: 305,
  SWITCH_PROXY: 306,
  TEMPORARY_REDIRECT: 307,
  PERMANENT_REDIRECT: 308,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  PAYMENT_REQUIRED: 402,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  METHOD_NOT_ALLOWED: 405,
  NOT_ACCEPTABLE: 406,
  PROXY_AUTHENTICATION_REQUIRED: 407,
  REQUEST_TIMEOUT: 408,
  CONFLICT: 409,
  GONE: 410,
  LENGTH_REQUIRED: 411,
  PRECONDITION_FAILED: 412,
  PAYLOAD_TOO_LARGE: 413,
  URI_TOO_LONG: 414,
  UNSUPPORTED_MEDIA_TYPE: 415,
  RANGE_NOT_SATISFIABLE: 416,
  EXPECTATION_FAILED: 417,
  I_AM_A_TEAPOT: 418,
  MISDIRECTED_REQUEST: 421,
  UNPROCESSABLE_ENTITY: 422,
  LOCKED: 423,
  FAILED_DEPENDENCY: 424,
  UPGRADE_REQUIRED: 426,
  PRECONDITION_REQUIRED: 428,
  TOO_MANY_REQUESTS: 429,
  REQUEST_HEADER_FIELDS_TOO_LARGE: 431,
  UNAVAILABLE_FOR_LEGAL_REASONS: 451,
  INTERNAL_SERVER_ERROR: 500,
  NOT_IMPLEMENTED: 501,
  BAD_GATEWAY: 502,
  SERVICE_UNAVAILABLE: 503,
  GATEWAY_TIMEOUT: 504,
  HTTP_VERSION_NOT_SUPPORTED: 505,
  VARIANT_ALSO_NEGOTIATES: 506,
  INSUFFICIENT_STORAGE: 507,
  LOOP_DETECTED: 508,
  NOT_EXTENDED: 510,
  NETWORK_AUTHENTICATION_REQUIRED: 511,
  // Note (Noah, 2024-05-31): This is a Cloudflare-specific code. It's not part of the standard
  // but Shopify uses it, so we're including it here since we sometimes have to process it.
  UPSTREAM_WEBSERVER_ERROR: 520,
} as const;

export type HttpStatusCode = ValueOfType<typeof HTTP_STATUS_CODES>;

/**
 * Common network error messages that browsers/runtimes return when network requests fail.
 * "Failed to fetch" - Chrome/Firefox when request fails due to network issues
 * "Load failed" - Safari when request fails due to network issues
 * "fetch failed" - Node/undici (and workerd) when the underlying connection fails
 * "ECONNREFUSED" - the connection-refused cause Node surfaces for a dead host
 */
const NETWORK_ERROR_MESSAGES = [
  "failed to fetch",
  "load failed",
  "fetch failed",
  "econnrefused",
] as const;

/**
 * Checks if an error message indicates a network error (e.g., connectivity issues,
 * request timeouts, DNS failures). These errors typically don't need to be tracked
 * in error monitoring systems like Sentry since they're user-side connectivity issues.
 */
export function isNetworkErrorMessage(message: string): boolean {
  const lowerMessage = message.toLowerCase();
  return NETWORK_ERROR_MESSAGES.some((networkError) => {
    return lowerMessage.includes(networkError);
  });
}

/**
 * Extracts a human-readable message from an unknown caught value. If the value
 * is an Error, returns its message; otherwise falls back to the provided
 * fallback string or `String(error)`.
 */
export function getErrorMessage(error: unknown, fallback?: string): string {
  if (error instanceof Error) {
    return error.message;
  }
  return fallback ?? String(error);
}
